import axios from 'axios';
import { supabase } from '../lib/supabase';
import { AgentConfig } from './aiConfigService';
import { LeadInsertData, SavedLead } from './leadsService';
import { getLanguageByCountry } from '../utils/countries';

interface BulkSendParams {
  // Pode ser LeadInsertData (do Dashboard) ou SavedLead (de Leads Salvos)
  leadsData: (LeadInsertData | SavedLead)[];
  intervalConfig: {
    minInterval: number;
    maxInterval: number;
  };
  agentConfig: AgentConfig;
  countryCode?: string; // Optional: 2-letter country code for DDI verification
}

export interface ValidationResult {
  validLeads: (LeadInsertData | SavedLead)[];
  blockedCount: number;
  pendingCount: number;
  invalidCount: number;
}

/**
 * Valida e filtra leads para envio em massa.
 * Retorna apenas leads com status 'Validado' e conta os bloqueados.
 * 
 * @param leads - Array de leads (LeadInsertData ou SavedLead)
 * @returns Objeto com leads validados e contagem de bloqueados
 */
export function validateLeadsForBulkSend(
  leads: (LeadInsertData | SavedLead)[]
): ValidationResult {
  let pendingCount = 0;
  let invalidCount = 0;

  const validLeads = leads.filter(lead => {
    // Para SavedLead, verificar whatsapp_status
    if ('whatsapp_status' in lead) {
      if (lead.whatsapp_status === 'Pendente') {
        pendingCount++;
        return false;
      }
      if (lead.whatsapp_status === 'Inválido') {
        invalidCount++;
        return false;
      }
      return lead.whatsapp_status === 'Validado';
    }

    // Para LeadInsertData (opcional), verificar whatsapp_status se existir
    if ('whatsapp_status' in lead && lead.whatsapp_status) {
      if (lead.whatsapp_status === 'Pendente') {
        pendingCount++;
        return false;
      }
      if (lead.whatsapp_status === 'Inválido') {
        invalidCount++;
        return false;
      }
      return lead.whatsapp_status === 'Validado';
    }

    // Se não tem whatsapp_status, considera como válido (para compatibilidade)
    return true;
  });

  return {
    validLeads,
    blockedCount: pendingCount + invalidCount,
    pendingCount,
    invalidCount,
  };
}

/**
 * Inicia o processo de envio em massa para os leads selecionados.
 * A requisição é enviada para uma Edge Function do Supabase para processamento seguro.
 */
export async function startBulkSend(params: BulkSendParams): Promise<{ message: string }> {
  const { leadsData, intervalConfig, agentConfig, countryCode } = params;

  // Sobrescrever idioma com base no país da busca (não altera a configuração salva)
  const detectedLanguage = countryCode ? getLanguageByCountry(countryCode) : null;
  const finalAgentConfig: AgentConfig = detectedLanguage
    ? {
      ...agentConfig,
      model_config: {
        ...agentConfig.model_config,
        selectedLanguage: detectedLanguage,
      },
    }
    : agentConfig;

  if (process.env.NODE_ENV === 'development' && detectedLanguage) {
    console.log(`[bulkSendService] Idioma detectado pelo país (${countryCode}): ${detectedLanguage}`);
  }

  // Validação de inputs
  if (!leadsData || leadsData.length === 0) {
    throw new Error('Nenhum lead selecionado para envio.');
  }

  if (!intervalConfig || !intervalConfig.minInterval || !intervalConfig.maxInterval) {
    throw new Error('Configuração de intervalo inválida.');
  }

  if (!agentConfig) {
    throw new Error('Configuração do agente não encontrada.');
  }

  if (process.env.NODE_ENV === 'development') {
    console.log('[bulkSendService] Iniciando envio em massa:', {
      leadsCount: leadsData.length,
      intervalConfig,
      agentConfigId: agentConfig.id
    });
  }

  // 1. Obter o token de sessão do Supabase
  const { data: { session } } = await supabase.auth.getSession();

  if (!session) {
    throw new Error('Usuário não autenticado. Por favor, faça login novamente.');
  }

  // 2. Chamar a Edge Function V2 (TESTE - Worker)
  const functionUrl = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/start-bulk-send-v2`;
  if (process.env.NODE_ENV === 'development') {
    console.log('[bulkSendService] 🧪 USANDO V2 (Worker) - Branch de teste');
  }

  try {
    if (process.env.NODE_ENV === 'development') {
      console.log('[bulkSendService] Chamando Edge Function:', functionUrl);
    }

    const response = await axios.post<{ message: string }>(
      functionUrl,
      {
        leadsData,
        intervalConfig,
        agentConfig: finalAgentConfig, // usa idioma sobrescrito se countryCode mapeado
        countryCode: params.countryCode,
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        timeout: 30000 // Timeout de 30s para iniciar o processo
      }
    );

    if (process.env.NODE_ENV === 'development') {
      console.log('[bulkSendService] Resposta da Edge Function:', response.data);
    }
    return response.data;

  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const message = error.response.data?.message || error.response.data || 'Erro desconhecido na Edge Function.';
      throw new Error(`Falha ao iniciar o envio em massa: ${message}`);
    }

    if (axios.isAxiosError(error) && error.code === 'ECONNABORTED') {
      throw new Error('Tempo limite excedido ao iniciar o envio. Por favor, tente novamente.');
    }

    throw new Error('Falha na comunicação com o servidor ao iniciar o envio.');
  }
}